import React, { useCallback, useState } from 'react'
import { Chip, Box } from '@mui/material'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import OpenWith from '@mui/icons-material/OpenWith'
import Close from '@mui/icons-material/Close'
import { grey, yellow } from '@mui/material/colors'
import { useDrag } from 'react-dnd'
import { useDispatch } from 'react-redux'

import { CaseOrCategory, CaseWithTitle, isCase } from './TriageCaseOrCategorySelect/types'
import { CaseCategory, usePersonCaseCategoriesById, useCases } from '~/entities/case'
import TriageCaseOrCategorySelectContainer from './TriageCaseOrCategorySelect/Container'
import { queueNotification } from '~/shared/notifications/actions'
import { useManageSelectedTodo } from '../useManageSelectedTodo'
import { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'

interface Props {
  person: Pick<BffCrmPersonChatRetrieveResponse['person'], 'id' | 'user'>
  selectedCategory: CaseCategory | null
  onCategorySelect: (selectedCategory: CaseCategory | null) => void
  disabled?: boolean
  threadUid: string | null
}

export const ChatCaseSelection: React.FC<Props> = ({
  selectedCategory,
  onCategorySelect,
  disabled = false,
  person,
  threadUid,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  // Use all categories for title lookup
  const { result: caseCategoriesById = {} } = usePersonCaseCategoriesById(null)
  const dispatch = useDispatch()
  const { selectedCaseId, navigateToCase } = useManageSelectedTodo()
  const { data: cases = [] } = useCases({ personId: person.id })

  // Derive the URL-focused case from the cases list and URL state
  const urlFocusedCase = selectedCaseId ? (cases.find(c => c.id === selectedCaseId) ?? null) : null
  const focusedCase: CaseWithTitle | null = urlFocusedCase
    ? {
        ...urlFocusedCase,
        title: caseCategoriesById[urlFocusedCase.category]?.title || `Case ${urlFocusedCase.id}`,
      }
    : null

  // Determine what to display: URL-focused case takes priority, then selected category
  const effectiveSelection: CaseOrCategory | null = focusedCase ?? selectedCategory

  let displayLabel: string | null = null
  if (effectiveSelection) displayLabel = effectiveSelection.title
  else displayLabel = 'Case'

  const handleChipClick = () => {
    if (!disabled) {
      setIsOpen(true)
    }
  }

  const handleClose = () => setIsOpen(false)

  const handleCaseOrCategorySelect = (newValue: CaseOrCategory) => {
    setIsOpen(false)

    if (!isCase(newValue)) {
      // Category selected - clear focused case and set category
      navigateToCase(null)
      onCategorySelect(newValue)
    } else {
      // Case selected - navigate to focus it and clear category
      navigateToCase(newValue.id)
      onCategorySelect(null)
    }
  }

  const handleClearCase = () => {
    if (urlFocusedCase) {
      // Clear the URL-focused case
      navigateToCase(null)
    } else if (selectedCategory) {
      // Clear the selected category
      onCategorySelect(null)
    }
  }

  // Drag and drop to a case to select it
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'caseSelection',
      item: () => ({
        type: 'caseSelection',
      }),
      collect: monitor => ({ isDragging: monitor.isDragging() }),
      end: (item, monitor) => {
        // The drop target will handle setting the case ID
        // We just need to check if the drop was successful
        if (monitor.didDrop()) {
          const dropResult = monitor.getDropResult() as { caseId?: number }
          if (dropResult?.caseId) {
            // Find the case object from the caseId
            const caseToSelect = cases.find(c => c.id === dropResult.caseId)
            if (caseToSelect) {
              // For drag and drop, navigate to the case and clear category
              navigateToCase(caseToSelect.id)
              onCategorySelect(null)
            } else {
              dispatch(queueNotification({ variant: 'error', message: 'Failed to find case' }))
            }
          }
        }
      },
    }),
    [onCategorySelect, cases, navigateToCase],
  )
  // The connector returns an element, which React 19's ref callbacks may not;
  // memoised so the wrapper keeps the connector's stable identity and the ref
  // is not detached and reattached on every render.
  const dragRef = useCallback((el: HTMLDivElement | null) => void drag(el), [drag])

  if (!isOpen) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box ref={dragRef} sx={{ p: 1 }}>
          <Box
            sx={{
              backgroundColor: grey[50],
              borderRadius: '50%',
              p: 0.6,
              transition: '100ms linear background-color',
              cursor: isDragging ? 'grabbing' : 'grab',
              opacity: isDragging ? 0.5 : 1,
              '&:hover': { backgroundColor: grey[200] },
            }}
          >
            <OpenWith fontSize="small" style={{ display: 'block', color: grey[600] }} />
          </Box>
        </Box>
        <Chip
          icon={<FolderOutlined style={{ color: grey[600] }} />}
          label={displayLabel}
          onClick={handleChipClick}
          deleteIcon={
            effectiveSelection ? <Close style={{ color: grey[600] }} fontSize="small" /> : undefined
          }
          onDelete={effectiveSelection ? handleClearCase : undefined}
          sx={{
            color: grey[700],
            fontSize: '1em',
            borderColor: effectiveSelection ? yellow[50] : grey[50],
            backgroundColor: effectiveSelection ? yellow[50] : grey[50],
            transition: '100ms linear all',
            minWidth: 0,
            '&:focus, &:hover': {
              backgroundColor: effectiveSelection ? yellow[50] : grey[50],
            },
            '&:focus': {
              borderColor: effectiveSelection ? yellow[400] : grey[400],
            },
          }}
          disabled={disabled}
          variant={effectiveSelection ? 'filled' : 'outlined'}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 1, mt: 0.33 }}>
      <TriageCaseOrCategorySelectContainer
        person={person}
        onChange={handleCaseOrCategorySelect}
        onBlur={handleClose}
        threadUid={threadUid}
      />
    </Box>
  )
}
